// Live selected-variant resolution for the product-page Quick form.
//
// The theme app extension's "Request a quote" button stamps the
// variant id at Liquid render time
// (product.selected_or_first_available_variant.id). Modern themes
// swap the variant WITHOUT a page reload when the shopper changes the
// selector, so that stamped attribute goes stale and the embed used to
// receive the default/first variant instead of the one the shopper is
// actually looking at.
//
// Fix lives here (embed side) so it covers BOTH the extension button
// and the legacy manual snippet with no Shopify deploy. At click time
// we resolve the LIVE selected variant from the page, preferring it
// over the stale id the button passed in. Precedence:
//   1. URL  ?variant=<id>      — canonical when present
//   2. product form [name="id"] — the theme's variant picker keeps
//      this in sync as the shopper changes options
//   3. the passed id           — Liquid default, only when neither
//      live source is found (non-standard themes, off-product callers)
//
// The pure resolver below is DOM-free so it can be unit-tested under
// node (vitest has no jsdom). The thin DOM reader that feeds it
// location.search + the form value lives at the openRequest() callsite
// in index.ts.

/** Coerce to a canonical numeric Shopify id string, or "" if the input
 *  isn't a positive integer. URL `?variant=` and the product form
 *  `[name="id"]` are always numeric on Shopify storefronts, so this
 *  rejects junk (empty `?variant=`, GIDs, garbage) before it can win
 *  over a real passed id. */
export function normalizeNumericId(raw: string | number | null | undefined): string {
  if (raw == null) return "";
  const s = String(raw).trim();
  return /^\d+$/.test(s) ? s : "";
}

/** Parse the `variant` query param out of a location.search string
 *  (leading "?" optional). Returns a normalized numeric id or "". */
export function variantFromSearch(search?: string | null): string {
  if (!search) return "";
  try {
    const q = search.charAt(0) === "?" ? search.slice(1) : search;
    return normalizeNumericId(new URLSearchParams(q).get("variant"));
  } catch {
    return "";
  }
}

/** Resolve the live selected variant id for a single-product prefill.
 *  URL ?variant= wins → product form [name="id"] → the passed id.
 *  The passed id is returned as-is (it may be a numeric string, a
 *  number, or a Shopify GID — the public-product endpoint normalizes
 *  all three downstream). Returns "" when nothing resolves. */
export function resolveLiveVariantId(opts: {
  /** location.search (with or without the leading "?"). */
  search?: string | null;
  /** value of the product form's [name="id"] input, if present. */
  formVariantId?: string | null;
  /** the variant id the theme button passed in (Liquid default). */
  passedVariantId?: string | number | null;
}): string {
  const fromUrl = variantFromSearch(opts.search);
  if (fromUrl) return fromUrl;
  const fromForm = normalizeNumericId(opts.formVariantId);
  if (fromForm) return fromForm;
  return opts.passedVariantId == null ? "" : String(opts.passedVariantId).trim();
}
